import { Button } from 'app/components/Base'
import { Dropdown } from 'app/components/Base/Dropdown'
import { getPharmacyDataUpdateUrl } from 'app/services/pharmacyDataUpdate'
import clsx from 'clsx'
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react'
import { Dataset } from '../constants/dataset'
import { DATASET_OPTIONS } from '../constants/datasetOptions'
import { PresignedUrlInfo } from './FileUploader'

type FetchDropdownProps = {
  disabled?: boolean
  setPresignedUrl: (v: PresignedUrlInfo | null) => void
  triggerType?: 'button' | 'change'
  buttonLabel?: string
}

export type FetchDropdownHandle = {
  reset: () => void
}

export const FetchDropdown = forwardRef<
  FetchDropdownHandle,
  FetchDropdownProps
>(function FetchDropdown(
  {
    disabled = false,
    setPresignedUrl,
    triggerType = 'button',
    buttonLabel = 'OK',
  }: FetchDropdownProps,
  ref,
) {
  const expiresRef = useRef<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState<Dataset | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  const clearExpires = () => {
    if (expiresRef.current) {
      clearTimeout(expiresRef.current)
    }
    expiresRef.current = null
  }

  useEffect(
    () => () => {
      if (expiresRef.current) {
        clearTimeout(expiresRef.current)
      }
    },
    [],
  )

  useImperativeHandle(ref, () => ({
    reset: () => {
      setSelected(undefined)
      setError(undefined)
      setIsLoading(false)
      clearExpires()
      setPresignedUrl(null)
    },
  }))

  const fetchData = async (dataset: Dataset) => {
    setIsLoading(true)
    try {
      const response = await getPharmacyDataUpdateUrl(dataset)
      clearExpires()
      setPresignedUrl({
        ...response,
        expiresCallback: clearExpires,
      })
      expiresRef.current = window.setTimeout(() => {
        setPresignedUrl(null)
        setSelected(undefined)
        setError(
          'アップロードURLの有効期限が切れました。もう一度、管理ファイル名を選択してください。',
        )
      }, response.expiresInSec * 1000)
    } catch {
      setError('S3 Presigned URLを発行できませんでした')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span>管理ファイル名</span>
      <div className="flex gap-2 flex-2">
        <div
          className={clsx('w-full', {
            'opacity-50 pointer-events-none': disabled || isLoading,
          })}
        >
          <Dropdown<Dataset>
            setValueDropdown={e => {
              if (disabled || isLoading) {
                return
              }
              if (e === selected) {
                return
              }
              setError(undefined)
              setSelected(e)
              if (triggerType === 'change') {
                fetchData(e)
              }
            }}
            valueDropdown={selected}
            optionDropdown={DATASET_OPTIONS}
            className={clsx('w-full', {
              'border-red-500': error,
              'border-gray-500': !error && selected,
              'border-gray-300': !error && !selected,
            })}
          />
        </div>
        {triggerType === 'button' && (
          <Button
            onClick={() => {
              setError(undefined)
              if (selected) {
                fetchData(selected)
              } else {
                setError('ファイル名を選択してください')
              }
            }}
            className="p-1 w-1/3"
            disabled={!selected || isLoading || disabled}
          >
            {isLoading ? 'Loading...' : buttonLabel}
          </Button>
        )}
      </div>
      {error && <div className="text-red-500">{error}</div>}
    </div>
  )
})
